// ==========================================
// 3. VIEW COMPONENT - TypeScript
// ==========================================
// view.component.ts

import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil, filter, switchMap } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { Payment_Gateway_Enum, PaymentGatewayModel } from 'src/app/models/portal';
import { selectWebsitePaymentGatewayByEnum } from 'src/app/shared/store/_features/website/payments/website-payments.selector';
import { SharedPortalViewService } from '../../shared-portal-view.service';
import { isWebsiteLoaded } from 'src/app/shared/store/_features/website/website.selector';
import { websitePaymentGatewaysActions } from 'src/app/shared/store/_features/website/payments/website-payments.actions';

@Component({
  selector: 'app-payment-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.css'],
})
export class ViewPortalPaymentsComponent implements OnInit, OnDestroy {
  @Input() gateway: Payment_Gateway_Enum;

  currentPaymentGateway: PaymentGatewayModel;
  isLoading = true;
  hasError = false;

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private title: Title,
    private sharedPortalViewService: SharedPortalViewService
  ) { }

  ngOnInit(): void {
    this.listenToGateway();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private listenToGateway(): void {
    this.store.select(isWebsiteLoaded).pipe(
      filter(loaded => loaded),
      switchMap(() => {
        if (!this.gateway) {
          const slug = this.route.snapshot.params['slug'];
          this.gateway = slug as Payment_Gateway_Enum;
        }

        return this.store.select(selectWebsitePaymentGatewayByEnum(this.gateway));
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (gateway) => {
        if (gateway) {
          this.currentPaymentGateway = gateway;
          this.updatePageTitle();
          this.isLoading = false;

          // Redirect if not integrated
          if (!gateway.integrated) {
            this.router.navigate(['../integrate'], { relativeTo: this.route });
          }
        } else {
          this.handleGatewayNotFound();
        }
      },
      error: () => this.handleLoadError()
    });
  }

  private updatePageTitle(): void {
    if (this.currentPaymentGateway) {
      this.title.setTitle(`${this.currentPaymentGateway.name} - Payment Settings`);
    }
  }

  private handleGatewayNotFound(): void {
    this.hasError = true;
    this.isLoading = false;
  }

  private handleLoadError(): void {
    this.hasError = true;
    this.isLoading = false;
  }

  revokePaymentGateway(): void {
    this.sharedPortalViewService.revokePaymentGatewayAlert(
      this.currentPaymentGateway,
      () => {
        this.store.dispatch(websitePaymentGatewaysActions.load());
      }
    );
  }

  navigateBack(): void {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }

  getAccountDetail(): string {
    switch (this.currentPaymentGateway.id) {
      case 'payPal':
        return this.currentPaymentGateway.email || 'Not set';
      case 'stripe':
        return this.currentPaymentGateway.accountId || 'Not set';
      case 'btcPay':
        return this.currentPaymentGateway.btcPayUrl || 'Not set';
      default:
        return 'N/A';
    }
  }
}
